/**
 * Site Clearance Certificate PDF — Acremitra
 *
 * A single-page A4 certificate that summarises the HT power-line and water-body
 * scan results for a property. Rendered on the branded Acre Mitra letterhead
 * so it slots into any deal packet the buyer gets (invoice, offer letter,
 * reservation letter). Buyers can also download it standalone from the
 * property detail page.
 *
 * Data model:
 *   property:  { title, location, latitude, longitude, price, area_display, type }
 *   ht:        the /api/properties/{id}/ht-wire-report payload
 *   waterbody: the /api/properties/{id}/waterbody-report payload
 */
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { COMPANY } from './company';

const LETTERHEAD_URL = '/letterhead.png';
const CONTENT_TOP = 145;
const CONTENT_BOTTOM = 155;

const BRAND = {
  navy: [15, 42, 68],
  navyDark: [7, 20, 36],
  emerald: [4, 120, 87],
  emeraldSoft: [209, 250, 229],
  amber: [252, 211, 77],
  amberSoft: [254, 243, 199],
  rose: [225, 29, 72],
  roseSoft: [255, 228, 230],
  sky: [2, 132, 199],
  skySoft: [224, 242, 254],
  slate: [71, 85, 105],
  slateLight: [148, 163, 184],
  ink: [15, 23, 42],
  border: [226, 232, 240],
  bg: [248, 250, 252],
};

// verdict → colour + label
const VERDICT_META = {
  critical: { color: BRAND.rose,    soft: BRAND.roseSoft,    label: 'CRITICAL',  emoji: '!' },
  warning:  { color: BRAND.amber,   soft: BRAND.amberSoft,   label: 'WARNING',   emoji: '~' },
  info:     { color: BRAND.sky,     soft: BRAND.skySoft,     label: 'INFO',      emoji: 'i' },
  clear:    { color: BRAND.emerald, soft: BRAND.emeraldSoft, label: 'CLEAR',     emoji: 'ok' },
  unknown:  { color: BRAND.slate,   soft: BRAND.bg,          label: 'UNKNOWN',   emoji: '?' },
};

const fmtDate = () =>
  new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

const shortId = (id) => (id ? String(id).slice(0, 8).toUpperCase() : 'PROPERTY');

const loadImageAsDataUrl = (url) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      try {
        const c = document.createElement('canvas');
        c.width = img.width; c.height = img.height;
        c.getContext('2d').drawImage(img, 0, 0);
        resolve(c.toDataURL('image/png'));
      } catch (_) { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });

const drawWatermark = (doc, w, h) => {
  doc.saveGraphicsState();
  doc.setGState(new doc.GState({ opacity: 0.05 }));
  doc.setTextColor(...BRAND.emerald);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(70);
  doc.text('SITE  CLEARANCE', w / 2, h / 2 + 30, { align: 'center', angle: -25 });
  doc.restoreGraphicsState();
};

// Render one big verdict card (HT or Water)
const drawVerdictCard = (doc, x, y, w, { title, kicker, verdict, headline, notes }) => {
  const v = VERDICT_META[verdict] || VERDICT_META.unknown;
  const cardH = 88;
  doc.setFillColor(...v.soft);
  doc.setDrawColor(...v.color);
  doc.setLineWidth(1.4);
  doc.roundedRect(x, y, w, cardH, 8, 8, 'FD');

  // kicker
  doc.setTextColor(...BRAND.slate);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.5);
  doc.text(kicker, x + 14, y + 14, { charSpace: 0.6 });

  // title + verdict pill
  doc.setTextColor(...BRAND.ink);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text(title, x + 14, y + 30);

  const pillW = 72, pillH = 20;
  const pillX = x + w - pillW - 14;
  const pillY = y + 14;
  doc.setFillColor(...v.color);
  doc.roundedRect(pillX, pillY, pillW, pillH, 10, 10, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text(v.label, pillX + pillW / 2, pillY + 13, { align: 'center', charSpace: 0.8 });

  // headline
  doc.setTextColor(...BRAND.ink);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.text(String(headline || '—'), x + 14, y + 50, { maxWidth: w - 28 });

  // notes
  doc.setTextColor(...BRAND.slate);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  const wrapped = doc.splitTextToSize(String(notes || ''), w - 28);
  doc.text(wrapped.slice(0, 3), x + 14, y + 66);
  return cardH;
};

export const generateSiteClearancePdf = async ({
  property = {},
  ht = null,
  waterbody = null,
  output = 'save',
}) => {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const MARGIN = 40;

  const letterhead = await loadImageAsDataUrl(LETTERHEAD_URL);
  if (letterhead) {
    try { doc.addImage(letterhead, 'PNG', 0, 0, W, H, undefined, 'SLOW'); } catch (_) {}
  }
  drawWatermark(doc, W, H);

  // ---- Header tag + certificate id (drawn INTO the letterhead's built-in
  // REFERENCE and DATE slots at PNG (58,793) and (718,793) — mapped to PDF
  // points via the 2382×3368 → 595×842 scale factor).
  const certId = `AM-SCC-${shortId(property.id)}`;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...BRAND.emerald);
  doc.text('SITE CLEARANCE CERTIFICATE · HT + WATER BODY SCAN', MARGIN, CONTENT_TOP - 8, { charSpace: 0.6 });

  // REFERENCE value (sits on top of the letterhead's dotted line)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...BRAND.ink);
  doc.text(certId, 95, 198);
  // DATE value (aligned with the letterhead's DATE label + line)
  doc.text(fmtDate(), 225, 198);

  // ---- Property block ----------------------------------------------------
  let y = CONTENT_TOP + 18;
  doc.setTextColor(...BRAND.slate);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.text('PROPERTY', MARGIN, y);
  y += 12;
  doc.setTextColor(...BRAND.ink);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text(property.title || 'Property', MARGIN, y, { maxWidth: W - MARGIN * 2 });
  y += 16;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(...BRAND.slate);
  const locLine = [property.location, property.area_display, property.type || property.property_type]
    .filter(Boolean).join(' · ');
  if (locLine) { doc.text(locLine, MARGIN, y, { maxWidth: W - MARGIN * 2 }); y += 12; }
  if (Number.isFinite(property.latitude) && Number.isFinite(property.longitude)) {
    doc.setFontSize(9);
    doc.text(`Pin · ${Number(property.latitude).toFixed(6)}, ${Number(property.longitude).toFixed(6)}`, MARGIN, y);
    y += 14;
  }

  // ---- Verdict cards -----------------------------------------------------
  y += 6;
  const cardW = W - MARGIN * 2;
  const htVerdict = ht?.verdict || 'unknown';
  const htLabel = ht?.verdict_label
    || (ht?.closest_distance_m != null
        ? `Nearest HT line ${Math.round(ht.closest_distance_m)} m away`
        : 'No HT infrastructure detected within 2 km');
  const htNotes = ht?.verdict_notes || (
    ht?.count > 0
      ? `${ht.count} HT line${ht.count !== 1 ? 's' : ''} mapped within a 2 km search radius from the plot pin.`
      : 'A 2 km scan of OpenStreetMap detected no traced high-tension transmission lines, substations or towers around this pin. Confirm on-site during your visit.'
  );
  const cardHtH = drawVerdictCard(doc, MARGIN, y, cardW, {
    kicker: 'HIGH-TENSION POWER LINES',
    title:  'HT clearance',
    verdict: htVerdict,
    headline: htLabel,
    notes:  htNotes,
  });
  y += cardHtH + 12;

  const wbVerdict = waterbody?.verdict || 'unknown';
  const wbLabel = waterbody?.verdict_label
    || (waterbody?.count > 0
        ? `${waterbody.count} water bod${waterbody.count !== 1 ? 'ies' : 'y'} detected within 500 m`
        : 'No water bodies within 500 m');
  const wbNotes = waterbody?.verdict_notes ||
    'A 500 m scan around the plot pin was run against OpenStreetMap. Water bodies within the buffer are listed below.';
  const cardWbH = drawVerdictCard(doc, MARGIN, y, cardW, {
    kicker: 'WATER BODIES · PONDS / RIVERS / RESERVOIRS',
    title:  'Water body clearance',
    verdict: wbVerdict,
    headline: wbLabel,
    notes:  wbNotes,
  });
  y += cardWbH + 16;

  // ---- Detail tables -----------------------------------------------------
  // HT lines
  const htLines = ht?.lines || [];
  const substations = ht?.substations || [];
  const towers = ht?.towers || [];
  const htBody = [];
  htLines.slice(0, 6).forEach((l) => htBody.push([
    `HT Line · ${l.voltage_label || (l.voltage_v ? Math.round(l.voltage_v / 1000) + ' kV' : 'HT')}`,
    l.operator || l.name || '—',
    `${Math.round(l.distance_m)} m`,
  ]));
  substations.slice(0, 3).forEach((s) => htBody.push([
    `Substation${s.voltage_label ? ' · ' + s.voltage_label : ''}`,
    s.name || s.operator || 'Substation',
    `${Math.round(s.distance_m)} m`,
  ]));
  if (towers.length) htBody.push([
    `HT Towers · ${towers.length} in radius`,
    'Nearest tower',
    `${Math.round(towers[0].distance_m)} m`,
  ]);
  if (htBody.length === 0) htBody.push(['—', 'No HT infrastructure mapped in 2 km', '—']);

  autoTable(doc, {
    startY: y,
    head: [['Type', 'Reference', 'Distance']],
    body: htBody,
    theme: 'grid',
    styles: {
      font: 'helvetica', fontSize: 8.5,
      cellPadding: { top: 5, right: 8, bottom: 5, left: 8 },
      textColor: BRAND.slate,
      lineColor: BRAND.border,
      lineWidth: 0.4,
    },
    headStyles: {
      fillColor: BRAND.navy, textColor: [255, 255, 255],
      fontStyle: 'bold', fontSize: 8, halign: 'left',
    },
    columnStyles: {
      0: { cellWidth: 155, fontStyle: 'bold', textColor: BRAND.ink },
      1: { cellWidth: 240 },
      2: { halign: 'right', cellWidth: 80, fontStyle: 'bold', textColor: BRAND.navy },
    },
    alternateRowStyles: { fillColor: BRAND.bg },
    margin: { left: MARGIN, right: MARGIN },
  });
  y = doc.lastAutoTable.finalY + 10;

  // Water body table
  const wbBody = (waterbody?.features || []).slice(0, 6).map((f) => [
    f.type || 'Water body',
    f.name || '—',
    `${Math.round(f.distance_m)} m`,
  ]);
  if (wbBody.length === 0) wbBody.push(['—', 'No water bodies mapped in 500 m', '—']);

  autoTable(doc, {
    startY: y,
    head: [['Type', 'Name', 'Distance']],
    body: wbBody,
    theme: 'grid',
    styles: {
      font: 'helvetica', fontSize: 8.5,
      cellPadding: { top: 5, right: 8, bottom: 5, left: 8 },
      textColor: BRAND.slate,
      lineColor: BRAND.border,
      lineWidth: 0.4,
    },
    headStyles: {
      fillColor: BRAND.sky, textColor: [255, 255, 255],
      fontStyle: 'bold', fontSize: 8, halign: 'left',
    },
    columnStyles: {
      0: { cellWidth: 155, fontStyle: 'bold', textColor: BRAND.ink },
      1: { cellWidth: 240 },
      2: { halign: 'right', cellWidth: 80, fontStyle: 'bold', textColor: BRAND.sky },
    },
    alternateRowStyles: { fillColor: BRAND.bg },
    margin: { left: MARGIN, right: MARGIN },
  });
  y = doc.lastAutoTable.finalY + 14;

  // ---- Data-source disclaimer -------------------------------------------
  if (y < H - CONTENT_BOTTOM - 60) {
    doc.setTextColor(...BRAND.slate);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.text('DATA SOURCE & LEGAL NOTES', MARGIN, y);
    y += 12;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    const disc = [
      '• Scan run against OpenStreetMap (Overpass API) — the largest open geospatial dataset. Distances are computed via Haversine + point-to-segment (accuracy < 1% at 500 m scale).',
      '• A "clear" verdict means no matching HT / water feature is TRACED in OSM within the search radius. On-site inspection is always recommended before construction.',
      '• A "warning / critical" verdict means the pin sits inside the statutory safety corridor — obtain a No-Objection from the local DisCom / drainage board before starting work.',
      '• This certificate is issued by ' + COMPANY.name + ' as a due-diligence aid and is not a substitute for statutory clearances issued by CEA / DisComs / municipal authorities.',
    ];
    disc.forEach((t) => {
      const lines = doc.splitTextToSize(t, W - MARGIN * 2);
      doc.text(lines, MARGIN, y);
      y += lines.length * 10;
    });
  }

  // ---- Signatory / footer ------------------------------------------------
  const sigY = H - CONTENT_BOTTOM - 30;
  doc.setDrawColor(...BRAND.border);
  doc.setLineWidth(0.5);
  doc.line(MARGIN, sigY, MARGIN + 140, sigY);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...BRAND.ink);
  doc.text(COMPANY.founder, MARGIN, sigY + 12);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...BRAND.slate);
  doc.text(COMPANY.founderTitle, MARGIN, sigY + 22);
  doc.text(COMPANY.name, MARGIN, sigY + 32);

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(7);
  doc.setTextColor(...BRAND.slateLight);
  doc.text('CONFIDENTIAL · For addressee only', MARGIN, H - CONTENT_BOTTOM + 8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND.emerald);
  doc.setFontSize(8);
  doc.text('acremitra.com', W - MARGIN, H - CONTENT_BOTTOM + 8, { align: 'right' });

  const fileName = `acremitra-clearance-${shortId(property.id).toLowerCase()}.pdf`;
  if (output === 'base64') return doc.output('datauristring');
  if (output === 'blob') return doc.output('blob');
  doc.save(fileName);
  return fileName;
};

export default generateSiteClearancePdf;
